import { ref, type Ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { config } from '../services/configService';
import { useDateFormatter } from './useDateFormatter';
import { validate } from '../contracts/validate';
import '../contracts/Despesas_SalvarInput';
import type { Despesas_SalvarInput } from '../contracts/Despesas_SalvarInput';
import type {
  DespesaRow,
  DespesaListItem,
  DespesaCategoria,
  DespesasApiResponse
} from '../types/despesas.types';
import '../contracts/Despesas_CarregaDespesasResponse';
import '../contracts/Despesas_GetAvailableDespesasForPayrollPeriodResponse';
import '../contracts/Despesas_BuscaDespesaResponse';
import '../contracts/Despesas_BuscaCategoriasDespesasResponse';
import '../contracts/Despesas_SalvarResponse';
import '../contracts/Despesas_CancelarDespesaResponse';
import '../contracts/Despesas_RemoverDespesaResponse';
import '../contracts/Despesas_BaixarDespesaResponse';
import type { Despesas_BuscaDespesaResponse } from '../contracts/Despesas_BuscaDespesaResponse';

/**
 * Override options for fetchDespesasWithOverride.
 * Bypasses the global session filter by supplying explicit franchise/date scope.
 */
export interface DespesasQueryOverrideOptions {
  queryOverride: {
    franquiaIds: Array<number | string>;
    dataInicio: string;
    dataFim: string;
  };
}

/**
 * Composable for Despesas (Expenses) operations
 * Provides methods to interact with the Despesas API
 *
 * TYPED DATA LAYER: Date handling follows the typed data layer pattern:
 * - DATE-ONLY fields: Use fromDateToApiDate (YYYY-MM-DD, no timezone conversion)
 * - DATETIME fields: Use toApiDateTimeFormat/fromApiDateTimeFormat (ISO 8601 UTC)
 * Date-only fields represent calendar dates, not moments in time.
 */
export function useDespesas() {
  const isLoading: Ref<boolean> = ref(false);
  const error: Ref<string | null> = ref(null);

  const errorMessage = (err: unknown): string =>
    err instanceof Error ? err.message : String(err);

  // Initialize date formatter for typed data layer conversions
  // DATE-ONLY: fromDateToApiDate (no timezone conversion)
  // DATETIME: toApiDateTimeFormat/fromApiDateTimeFormat (UTC conversion)
  const {
    fromDateToApiDate,
    localDateTimeToApi
  } = useDateFormatter();

  /**
   * Fetch all despesas — listing endpoint with aliased columns
   * (see DespesaListItem in src/types/despesas.types.ts).
   */
  const fetchDespesas = async (): Promise<DespesaListItem[]> => {
    log('useDespesas', 'fetchDespesas', 'Fetching all despesas');
    isLoading.value = true;
    error.value = null;

    try {
      const timestamp = new Date().getTime();
      const response = await httpService.get<DespesasApiResponse<DespesaListItem[]>>(
        `/index.php/despesas/carrega_despesas?_t=${timestamp}`,
        { responseContract: 'Despesas_CarregaDespesasResponse' }
      );
      log('useDespesas', 'fetchDespesas', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao buscar despesas');
      }
    } catch (err) {
      log('useDespesas', 'fetchDespesas', 'Error', err);
      error.value = errorMessage(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch despesas with query override (bypassing global filter)
   * Used to fetch despesas for a specific franchise and date range.
   */
  const fetchDespesasWithOverride = async (
    options: DespesasQueryOverrideOptions
  ): Promise<DespesaListItem[]> => {
    log('useDespesas', 'fetchDespesasWithOverride', 'Fetching despesas with override', options);
    isLoading.value = true;
    error.value = null;

    try {
      if (!options.queryOverride) {
        throw new Error('queryOverride is required for fetchDespesasWithOverride');
      }

      const timestamp = new Date().getTime();
      const response = await httpService.getWithOverride<DespesasApiResponse<DespesaListItem[]>>(
        `/index.php/despesas/carrega_despesas?_t=${timestamp}`,
        options.queryOverride,
        { responseContract: 'Despesas_CarregaDespesasResponse' }
      );
      log('useDespesas', 'fetchDespesasWithOverride', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao buscar despesas');
      }
    } catch (err) {
      log('useDespesas', 'fetchDespesasWithOverride', 'Error', err);
      error.value = errorMessage(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch available despesas for payroll period processing.
   * Used exclusively by the payroll wizard to fetch expenses not yet assigned
   * to a payroll period.
   *
   * @param franchiseId Franchise ID
   * @param startDate Start date (Date or 'YYYY-MM-DD' string)
   * @param endDate End date (Date or 'YYYY-MM-DD' string)
   * @param frequency Optional period frequency (weekly, biweekly, monthly) — filters
   *                  out same-frequency payroll-generated despesas
   */
  const fetchAvailableDespesasForPayrollPeriod = async (
    franchiseId: number,
    startDate: Date | string,
    endDate: Date | string,
    frequency: string | null = null
  ): Promise<DespesaListItem[]> => {
    log('useDespesas', 'fetchAvailableDespesasForPayrollPeriod', 'Fetching available despesas for payroll', { franchiseId, startDate, endDate, frequency });
    isLoading.value = true;
    error.value = null;

    try {
      const formattedStartDate = startDate instanceof Date ? fromDateToApiDate(startDate) : startDate;
      const formattedEndDate = endDate instanceof Date ? fromDateToApiDate(endDate) : endDate;

      let url = `/index.php/despesas/getAvailableDespesasForPayrollPeriod/${franchiseId}/${formattedStartDate}/${formattedEndDate}`;
      if (frequency) {
        url += `/${frequency}`;
      }

      const timestamp = new Date().getTime();
      const response = await httpService.get<DespesasApiResponse<DespesaListItem[]>>(
        `${url}?_t=${timestamp}`,
        { responseContract: 'Despesas_GetAvailableDespesasForPayrollPeriodResponse' }
      );
      log('useDespesas', 'fetchAvailableDespesasForPayrollPeriod', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao buscar despesas');
      }
    } catch (err) {
      log('useDespesas', 'fetchAvailableDespesasForPayrollPeriod', 'Error', err);
      error.value = errorMessage(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch a single despesa by ID — returns the raw record shape.
   * (DespesaRow: prefixed despesa_* fields, single record from buscaDespesa.)
   */
  const fetchDespesa = async (despesaId: number): Promise<DespesaRow | null> => {
    log('useDespesas', 'fetchDespesa', 'Fetching despesa', despesaId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<DespesasApiResponse<DespesaRow>>(
        `/index.php/despesas/buscaDespesa/${despesaId}`,
        { responseContract: 'Despesas_BuscaDespesaResponse' }
      );
      log('useDespesas', 'fetchDespesa', 'Response', response);

      if (response.success) {
        return response.dados || null;
      } else {
        throw new Error(response.error || 'Erro ao buscar despesa');
      }
    } catch (err) {
      log('useDespesas', 'fetchDespesa', 'Error', err);
      error.value = errorMessage(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch the despesa categories used by the form select / CSV import auto-matcher.
   * Only active categories come back; `incluirCategoriaId` keeps the current
   * category of a despesa being edited even when it is inactive.
   */
  const fetchCategorias = async (incluirCategoriaId?: string | number): Promise<DespesaCategoria[]> => {
    log('useDespesas', 'fetchCategorias', 'Fetching categories');
    isLoading.value = true;
    error.value = null;

    try {
      const url = incluirCategoriaId
        ? `/index.php/despesas/buscaCategoriasDespesas/${encodeURIComponent(String(incluirCategoriaId))}`
        : '/index.php/despesas/buscaCategoriasDespesas';
      const response = await httpService.get<DespesasApiResponse<DespesaCategoria[]>>(
        url,
        { responseContract: 'Despesas_BuscaCategoriasDespesasResponse' }
      );
      log('useDespesas', 'fetchCategorias', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao buscar categorias');
      }
    } catch (err) {
      log('useDespesas', 'fetchCategorias', 'Error', err);
      error.value = errorMessage(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Save despesa (create or update). Validates the payload against the
   * Despesas_SalvarInput contract before posting.
   */
  const saveDespesa = async (
    input: Despesas_SalvarInput
  ): Promise<DespesasApiResponse<DespesaRow>> => {
    log('useDespesas', 'saveDespesa', 'Saving despesa', input);
    isLoading.value = true;
    error.value = null;

    try {
      const endpoint = `${config.api.sistemaHost}/despesas/salvar`;
      // App → API boundary: browser-local datetime → ISO 8601 UTC
      // (despesa_data_vencimento is DATE-only and stays as is)
      const despesaData: Despesas_SalvarInput = {
        ...input,
        ...(input.despesa_data_lancamento
          ? { despesa_data_lancamento: localDateTimeToApi(input.despesa_data_lancamento) }
          : {}),
      };

      validate('Despesas_SalvarInput', despesaData as unknown as Record<string, unknown>);
      const response = await httpService.post<DespesasApiResponse<DespesaRow>>(endpoint, despesaData, {
        responseContract: 'Despesas_SalvarResponse',
      });
      log('useDespesas', 'saveDespesa', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.message || response.error || 'Erro ao salvar despesa');
      }
    } catch (err) {
      log('useDespesas', 'saveDespesa', 'Error', err);
      error.value = errorMessage(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Internal helper for the four ID-only mutating endpoints
   * (cancela / reativa / remove / baixa). All four expect a path-bound
   * despesa_id and return the same envelope.
   */
  const callDespesaAction = async (
    endpointPath: string,
    despesaId: number,
    errorPrefix: string
  ): Promise<DespesasApiResponse<unknown>> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post<DespesasApiResponse<unknown>>(
        `${config.api.sistemaHost}/${endpointPath}/${despesaId}`,
        null
      );

      if (response.success) {
        return response;
      } else {
        throw new Error(response.message || response.error || errorPrefix);
      }
    } catch (err) {
      error.value = errorMessage(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /** Cancel despesa */
  const cancelarDespesa = (despesaId: number): Promise<DespesasApiResponse<unknown>> => {
    log('useDespesas', 'cancelarDespesa', 'Cancelling despesa', despesaId);
    isLoading.value = true;
    error.value = null;

    return httpService.post<DespesasApiResponse<unknown>>(
      `${config.api.sistemaHost}/despesas/cancelar_despesa/${despesaId}`,
      null,
      { responseContract: 'Despesas_CancelarDespesaResponse' }
    ).then((response) => {
      if (response.success) {
        return response;
      } else {
        throw new Error(response.message || response.error || 'Erro ao cancelar despesa');
      }
    }).catch((err) => {
      error.value = errorMessage(err);
      throw err;
    }).finally(() => {
      isLoading.value = false;
    });
  };

  /** Reactivate despesa */
  const reativarDespesa = (despesaId: number): Promise<DespesasApiResponse<unknown>> => {
    log('useDespesas', 'reativarDespesa', 'Reactivating despesa', despesaId);
    return callDespesaAction('despesas/reativa_despesa', despesaId, 'Erro ao reativar despesa');
  };

  /** Remove despesa */
  const removerDespesa = (despesaId: number): Promise<DespesasApiResponse<unknown>> => {
    log('useDespesas', 'removerDespesa', 'Removing despesa', despesaId);
    isLoading.value = true;
    error.value = null;

    return httpService.post<DespesasApiResponse<unknown>>(
      `${config.api.sistemaHost}/despesas/remover_despesa/${despesaId}`,
      null,
      { responseContract: 'Despesas_RemoverDespesaResponse' }
    ).then((response) => {
      if (response.success) {
        return response;
      } else {
        throw new Error(response.message || response.error || 'Erro ao remover despesa');
      }
    }).catch((err) => {
      error.value = errorMessage(err);
      throw err;
    }).finally(() => {
      isLoading.value = false;
    });
  };

  /** Pay despesa (baixar) */
  const baixarDespesa = (despesaId: number): Promise<DespesasApiResponse<unknown>> => {
    log('useDespesas', 'baixarDespesa', 'Paying despesa', despesaId);
    isLoading.value = true;
    error.value = null;

    return httpService.post<DespesasApiResponse<unknown>>(
      `${config.api.sistemaHost}/despesas/baixar_despesa/${despesaId}`,
      null,
      { responseContract: 'Despesas_BaixarDespesaResponse' }
    ).then((response) => {
      if (response.success) {
        return response;
      } else {
        throw new Error(response.message || response.error || 'Erro ao baixar despesa');
      }
    }).catch((err) => {
      error.value = errorMessage(err);
      throw err;
    }).finally(() => {
      isLoading.value = false;
    });
  };

  return {
    // State
    isLoading,
    error,

    // Methods
    fetchDespesas,
    fetchDespesasWithOverride,
    fetchAvailableDespesasForPayrollPeriod,
    fetchDespesa,
    fetchCategorias,
    saveDespesa,
    cancelarDespesa,
    reativarDespesa,
    removerDespesa,
    baixarDespesa
  };
}
